import type { Order } from '@/services/orderService'
import type { BillingInfo } from '@/types/invoiceInfo'
import { JOSCOWORK_COMPANY } from '@/lib/joscoworkCompany'
import { thaiBahtText } from '@/lib/thaiBahtText'

// เลย์เอาต์อ้างอิงไฟล์ต้นแบบ "ใบเสร็จรับเงิน และใบกำกับภาษี อบรม.xlsx" แต่จัดใหม่แบบ editorial —
// ลดเส้นขอบ/เส้นคั่นลงมาก ใช้ระยะห่าง/ลำดับชั้นตัวอักษร/การจัดวางแทนการตีเส้นแบ่งทุกส่วน (2026-09-30)
// ข้อมูลผู้สั่งซื้อ + เลขที่เอกสาร มาจาก order.billing_info ที่ backend บันทึกถาวรไว้ตอนจ่ายเงินสำเร็จแล้ว

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'

const money = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const recipientDisplay = (info: BillingInfo | null) => {
  if (!info) return { name: '-', address: '-', taxId: '' }
  if (info.recipient_type === 'COMPANY') {
    return {
      name: info.company_name || '-',
      address: [info.address, info.subdistrict, info.district, info.province, info.postal_code].filter(Boolean).join(' '),
      taxId: info.tax_id ? `${info.tax_id}${info.branch_code ? ` (สาขา ${info.branch_code})` : ''}` : '',
    }
  }
  return {
    name: info.full_name || '-',
    address: [info.address, info.subdistrict, info.district, info.province, info.postal_code].filter(Boolean).join(' '),
    taxId: info.tax_id || '',
  }
}

export default function ReceiptCard({ order }: { order: Order }) {
  const recipient = recipientDisplay(order.billing_info)
  const docNumber = order.billing_info?.doc_number || '-'
  const subtotal = order.total_amount
  const discount = order.discount || 0
  const afterDiscount = subtotal - discount
  const vat = Math.round(afterDiscount * 0.07 * 100) / 100
  const grandTotal = order.final_amount

  // ระบบชำระเงินตอนนี้มีแค่ออนไลน์/ฟรี ยังไม่มี "เงินสด" เป็นตัวเลือกจริง — ติ๊กช่อง "เงินโอน" แทนทุกวิธีที่ไม่ใช่เงินสด
  const paymentMethod = order.payment?.payment_method
  const isCash = false
  const isTransfer = !isCash && !!paymentMethod

  return (
    <div
      className="receipt-print-area mx-auto bg-white text-[13px] leading-relaxed text-[#1a1a1a]"
      style={{ width: '210mm', minHeight: '297mm', fontFamily: "'Sarabun', sans-serif", padding: '20mm 18mm' }}
    >
      {/* หัวกระดาษ — บริษัทซ้าย / เอกสารขวา ไม่มีเส้นคั่น ใช้ระยะห่างแทน */}
      <div className="flex items-start justify-between">
        <div className="leading-[1.6]">
          <p className="text-[15px] font-bold">{JOSCOWORK_COMPANY.name}</p>
          <p className="text-[#555]">{JOSCOWORK_COMPANY.addressLine1}</p>
          <p className="text-[#555]">{JOSCOWORK_COMPANY.addressLine2} · Tel. {JOSCOWORK_COMPANY.tel}</p>
          <p className="text-[#555]">เลขประจำตัวผู้เสียภาษี {JOSCOWORK_COMPANY.taxId}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[11px] uppercase tracking-wide text-[#888]">Original / ต้นฉบับ</p>
          <p className="mt-1 text-lg font-bold uppercase tracking-wide">Tax Invoice / Receipt</p>
          <p className="text-[#555]">ใบกำกับภาษี/ใบเสร็จรับเงิน</p>
          <p className="mt-2 text-[#555]">เลขที่ <span className="font-semibold text-[#1a1a1a]">{docNumber}</span></p>
        </div>
      </div>

      {/* ข้อมูลลูกค้า — ไม่มีกรอบเลย ใช้ label ตัวพิมพ์เล็กสีอ่อนแยกจากค่า */}
      <div className="mt-10 flex items-start justify-between gap-8">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[#888]">ชื่อลูกค้า</p>
          <p className="mt-0.5 font-medium">{recipient.name}</p>
          {recipient.address && <p className="mt-1.5 text-[#555]">{recipient.address}</p>}
          {recipient.taxId && <p className="text-[#555]">เลขประจำตัวผู้เสียภาษี {recipient.taxId}</p>}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[11px] uppercase tracking-wide text-[#888]">วันที่</p>
          <p className="mt-0.5 font-medium">{formatDate(order.paid_at || order.created_date)}</p>
        </div>
      </div>

      {/* ตารางรายการ — เส้นบางแค่ใต้หัวตาราง เส้นเดียว ไม่มีเส้นแบ่งแต่ละแถว */}
      <table className="mt-10 w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-[#1a1a1a]">
            <th className="w-10 pb-2 text-[11px] font-semibold uppercase tracking-wide text-[#888]">No.</th>
            <th className="pb-2 text-[11px] font-semibold uppercase tracking-wide text-[#888]">รายการ</th>
            <th className="w-16 pb-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#888]">จำนวน</th>
            <th className="w-28 pb-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#888]">ราคา/หน่วย</th>
            <th className="w-28 pb-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#888]">จำนวนเงิน</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((item, i) => (
            <tr key={item.order_item_id} className="align-top">
              <td className="pt-3">{i + 1}</td>
              <td className="pt-3">{item.item_name}</td>
              <td className="pt-3 text-right tabular-nums">{item.quantity}</td>
              <td className="pt-3 text-right tabular-nums">{money(item.unit_price)}</td>
              <td className="pt-3 text-right tabular-nums">{money(item.subtotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* สรุปยอด — จัดชิดขวา ไม่มีกรอบ เว้นระยะให้ยอดรวมสุทธิเด่นชัดกว่าตัวอื่นมาก */}
      <div className="mt-8 flex justify-end">
        <div className="w-72 space-y-2 text-right">
          <div className="flex justify-between text-[#555]">
            <span>รวมเป็นเงิน</span>
            <span className="tabular-nums">฿{money(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-[#555]">
              <span>ส่วนลด</span>
              <span className="tabular-nums">-฿{money(discount)}</span>
            </div>
          )}
          <div className="flex justify-between text-[#555]">
            <span>ภาษีมูลค่าเพิ่ม 7%</span>
            <span className="tabular-nums">฿{money(vat)}</span>
          </div>
          <div className="mt-3 flex items-baseline justify-between border-t border-[#1a1a1a] pt-3">
            <span className="text-sm font-semibold uppercase tracking-wide">รวมทั้งสิ้น</span>
            <span className="text-2xl font-bold tabular-nums">฿{money(grandTotal)}</span>
          </div>
        </div>
      </div>

      <p className="mt-6 text-right text-[#555]">{thaiBahtText(grandTotal)}</p>

      {/* วิธีชำระเงิน + หมายเหตุ — เส้นบางเส้นเดียวคั่นจากส่วนยอดเงิน */}
      <div className="mt-12 border-t border-[#E5E5E5] pt-6 text-[#555]">
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          <span>({isCash ? '✓' : ' '}) เงินสด</span>
          <span>({isTransfer ? '✓' : ' '}) เงินโอน</span>
          <span>( ) เช็ค ธนาคาร _________________ เลขที่เช็ค _________________ วันที่ _________________</span>
        </div>
        <p className="mt-4 text-xs">ชำระผ่านระบบออนไลน์ — เอกสารนี้ออกโดยอัตโนมัติ</p>
      </div>
    </div>
  )
}
